import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Sparkle, ArrowRight, CheckCircle, Copy, Download } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Rewrite service modal.
 * - Explains the deeper Rewrite pass + flat £10 fee
 * - On submit:
 *    • Admin → runs immediately (auto_run from /rewrite/checkout)
 *    • Non-admin → opens Stripe checkout URL
 * - Displays the rewritten text inline when ready
 */
export const RewriteModal = ({ open, onClose, sessionId, isAdmin }) => {
  const [submitting, setSubmitting] = useState(false);
  const [rewriteText, setRewriteText] = useState('');
  const [progress, setProgress] = useState('');

  useEffect(() => {
    if (!open) return;
    // If a previous rewrite exists for this session, hydrate it
    if (!sessionId) return;
    let cancelled = false;
    axios
      .get(`${API}/rewrite/${sessionId}`, { withCredentials: true })
      .then((r) => {
        if (!cancelled && r.data?.rewrite_text) setRewriteText(r.data.rewrite_text);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, sessionId]);

  // Reset transient state when the modal is closed
  useEffect(() => {
    if (open) return;
    const t = setTimeout(() => {
      setRewriteText('');
      setProgress('');
      setSubmitting(false);
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  const handleStart = async () => {
    if (!sessionId) {
      toast.error('No session — proofread a document first.');
      return;
    }
    setSubmitting(true);
    setProgress(isAdmin ? 'Rewriting your prose — this can take 60–120 seconds...' : 'Opening secure checkout...');

    // Poll the GET endpoint until rewrite_text is populated. Used as a fallback
    // when the original POST hits the K8s ingress 60s timeout (502/504) even
    // though the backend is still working and will persist the result.
    const pollForRewrite = async (maxMs = 240000, intervalMs = 3000) => {
      const deadline = Date.now() + maxMs;
      while (Date.now() < deadline) {
        try {
          const r = await axios.get(`${API}/rewrite/${sessionId}`, { withCredentials: true });
          if (r.data?.rewrite_text) return r.data.rewrite_text;
        } catch (_) { /* keep polling */ }
        await new Promise((res) => setTimeout(res, intervalMs));
      }
      return '';
    };

    try {
      const r = await axios.post(
        `${API}/rewrite/checkout/${sessionId}`,
        {},
        { withCredentials: true, timeout: 480000 }
      );

      if (r.data?.already_completed && r.data?.rewrite_text) {
        setRewriteText(r.data.rewrite_text);
        toast.success('Existing rewrite loaded');
        return;
      }
      if (r.data?.auto_run && r.data?.rewrite_text) {
        setRewriteText(r.data.rewrite_text);
        toast.success('Rewrite complete');
        return;
      }
      if (r.data?.url) {
        // Non-admin → Stripe Checkout
        window.location.href = r.data.url;
        return;
      }
      throw new Error('Unexpected response from server');
    } catch (err) {
      // Ingress timeout (502/504/network) → backend may still be running. Poll
      // the rewrite endpoint until the text is persisted, then show it.
      const isTimeout =
        !err.response ||
        err.response.status === 502 ||
        err.response.status === 504 ||
        err.code === 'ECONNABORTED';
      if (isTimeout) {
        toast.message('Connection paused — recovering your rewrite from the server...');
        setProgress('Recovering rewrite — almost there...');
        const text = await pollForRewrite();
        if (text) {
          setRewriteText(text);
          toast.success('Rewrite complete');
          return;
        }
      }
      const detail = err.response?.data?.detail || err.message || 'Rewrite failed';
      toast.error(String(detail).slice(0, 160));
    } finally {
      setSubmitting(false);
      setProgress('');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(rewriteText);
    toast.success('Rewrite copied');
  };

  const handleDownload = () => {
    const blob = new Blob([rewriteText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rewrite_${sessionId?.slice(0, 8) || 'session'}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] bg-card border-border text-ivory"
        data-testid="rewrite-modal"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-serif text-2xl tracking-tight flex items-center gap-3">
            <Sparkle size={22} weight="duotone" className="text-gold" />
            Rewrite service
          </DialogTitle>
          <p className="text-sm text-bone font-sans mt-1">
            A deeper, comprehensive pass for prose that wants more than proofreading.
          </p>
        </DialogHeader>

        {!rewriteText ? (
          <div className="space-y-5 overflow-y-auto pr-1">
            <div className="rounded-lg border border-border bg-stone/30 p-5">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-3">What you get</p>
              <ul className="space-y-2.5 text-sm text-bone leading-relaxed">
                {[
                  'Improved clarity, sentence rhythm and flow',
                  'Smoother transitions and pacing',
                  'Tightened prose — weak verbs replaced, awkward phrasing fixed',
                  'Your voice, characters, plot and tone preserved exactly',
                  'Delivered as a clean, downloadable .txt you can paste into your manuscript',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5">
                    <CheckCircle size={14} weight="duotone" className="text-gold mt-1 shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`rounded-lg border p-5 flex items-center justify-between gap-4 ${isAdmin ? 'border-emerald-400/40 bg-emerald-400/5' : 'border-gold/40 bg-gold/5'}`}>
              <div>
                <p className={`text-[10px] font-medium uppercase tracking-[0.28em] mb-1 ${isAdmin ? 'text-emerald-300' : 'text-gold'}`}>
                  {isAdmin ? 'Host · Complimentary' : 'Flat rate'}
                </p>
                <p className="font-serif text-3xl text-ivory leading-none">{isAdmin ? 'Free' : '£10'}</p>
                <p className="text-xs text-bone mt-1">
                  {isAdmin
                    ? 'All services are complimentary for the host account.'
                    : 'One-off · any document length · secure Stripe checkout'}
                </p>
              </div>
              <Sparkle size={32} weight="duotone" className={isAdmin ? 'text-emerald-300/70' : 'text-gold/70'} />
            </div>

            <p className="text-xs text-ash leading-relaxed">
              Rewrites take about 60–120 seconds depending on length. Songs are excluded — songwriter analysis is already preserved verbatim by design.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-gold px-4 py-3 rounded-md hover:bg-stone transition-colors"
                data-testid="rewrite-cancel-btn"
              >
                Maybe later
              </button>
              <button
                type="button"
                onClick={handleStart}
                disabled={submitting}
                className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-6 py-3 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-60 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
                data-testid="rewrite-confirm-btn"
              >
                {submitting ? (progress || 'Starting...') : (isAdmin ? 'Run rewrite' : 'Continue to checkout · £10')}
                {!submitting && <ArrowRight size={12} weight="bold" />}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 overflow-y-auto pr-1" data-testid="rewrite-result">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold">Rewritten prose</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-gold px-3 py-2 rounded-md hover:bg-stone transition-colors inline-flex items-center gap-1.5"
                  data-testid="rewrite-copy-btn"
                >
                  <Copy size={12} weight="duotone" /> Copy
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-gold px-3 py-2 rounded-md hover:bg-stone transition-colors inline-flex items-center gap-1.5"
                  data-testid="rewrite-download-btn"
                >
                  <Download size={12} weight="duotone" /> Download
                </button>
              </div>
            </div>
            <div
              className="rounded-lg border border-border bg-stone/20 p-5 max-h-[55vh] overflow-y-auto font-serif text-ivory leading-[1.85] whitespace-pre-wrap text-[15px]"
              data-testid="rewrite-text-pane"
            >
              {rewriteText}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
